import React, { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';
import { useHistory } from 'react-router-dom';
import { apiV2Instance } from '../../common/lib/api-v2.client';
import { useUserData } from '../../common/contexts/user-data.context';
import { qcSurfaceCardSx } from '../../common/styles/page-title';
import QuickLinksGiftBar from '../quick-links/quick-links-offer';
import {
  memberGiftButtons,
  OpenMemberRequest,
  previewActOut,
  requestSentCopy,
} from '../premium-profile/member-request-buttons';
import ProfileSignupPrompt from '../premium-profile/signup-prompt';
import ProfileFooter from './profile-footer';
import ProfileMenu from './profile-menu';
import PublisherMark from './publisher-mark';
import { PublicQuickLink } from './quick-link-list';
import { deviceLabel } from './device-label';
import { leaveCopy } from './profile-join';
import { rememberQuickLinksOwner } from '../premium-profile/quicklinks-attribution';
import ProfileIdentityCard from './profile-identity-card';
import {
  connectionStatPath,
  RelationshipKey,
} from '../home/routes/connections/relationship-nav';

type PublicGiftCounts = {
  available: number;
  redeemed: number;
};

type PublicProfile = {
  ownerId?: string;
  channelName: string;
  displayName: string;
  profileImageURL: string;
  about: string;
  owned: boolean;
  links: PublicQuickLink[];
  stats?: {
    referred: number;
    following: number;
    followers: number;
    favorites: number;
    favorited: number;
    gifted?: number | null;
  };
  giftAvailable?: boolean;
  giftCode?: string;
  visitorAutoGive?: boolean;
  giveMembers?: boolean;
  singleRemaining?: number;
  dualRemaining?: number;
  badge?: { code: string; name: string };
  giftCards?: {
    single: PublicGiftCounts;
    dual: PublicGiftCounts;
  };
};

type PublicProfilePageProps = {
  channelName: string;
  audience?: 'member' | 'visitor';
  countVisit?: boolean;
  refreshToken?: number;
};

type LinkedStat = RelationshipKey;

export { connectionStatPath };

function readError(error: unknown, fallback: string): string {
  const message = (
    error as { response?: { data?: { message?: string | string[] } } }
  )?.response?.data?.message;
  if (Array.isArray(message)) return message.join(' ');
  return typeof message === 'string' && message ? message : fallback;
}

function PublicProfilePage({
  channelName,
  audience,
  countVisit = true,
  refreshToken = 0,
}: PublicProfilePageProps): JSX.Element {
  const history = useHistory();
  const { auth, user } = useUserData();
  const signedIn = Boolean(auth?.token);
  const memberView = (audience || (signedIn ? 'member' : 'visitor')) === 'member';
  const ownProfile = Boolean(
    user?.channelName?.trim()
    && user.channelName.trim() === channelName.trim(),
  );
  const [profile, setProfile] = useState<PublicProfile | null>(null);
  const [missing, setMissing] = useState(false);
  const [error, setError] = useState('');
  const [eligible, setEligible] = useState(false);
  const [serverRequest, setServerRequest] = useState<OpenMemberRequest>(null);
  const [previewRequest, setPreviewRequest] = useState<OpenMemberRequest>(null);
  const [sentScope, setSentScope] = useState<'SINGLE' | 'DUAL' | null>(null);
  const [signupOpen, setSignupOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [leaveKind, setLeaveKind] = useState<'stats' | 'reserve' | null>(null);
  const [giftOpener, setGiftOpener] = useState(0);
  const seenProfile = useRef(false);

  useEffect(() => {
    let cancelled = false;
    if (countVisit) {
      apiV2Instance
        .post('/api/quick-links/analytics/session', {}, { withCredentials: true })
        .catch(() => undefined);
    }
    apiV2Instance
      .get(`/api/premium-profile/public/${encodeURIComponent(channelName)}`)
      .then((response) => {
        if (cancelled) return;
        seenProfile.current = true;
        setProfile(response.data);
        setMissing(false);
        if (!countVisit) return;
        apiV2Instance
          .post(
            '/api/quick-links/analytics/view',
            {
              channelName,
              marker: 'quickcam-profile',
              device: deviceLabel(),
            },
            { withCredentials: true },
          )
          .catch(() => undefined);
      })
      .catch(() => {
        if (!cancelled && !seenProfile.current) setMissing(true);
      });
    return () => {
      cancelled = true;
    };
  }, [channelName, countVisit, refreshToken]);

  useEffect(() => {
    if (!memberView || !profile?.owned) return undefined;
    let cancelled = false;
    apiV2Instance
      .get(
        `/api/premium-profile/request-eligibility/${encodeURIComponent(channelName)}`,
      )
      .then((response) => {
        if (!cancelled) {
          setEligible(Boolean(response.data?.eligible));
          setServerRequest(response.data?.openRequest || null);
        }
      })
      .catch(() => {
        if (!cancelled) setEligible(false);
      });
    return () => {
      cancelled = true;
    };
  }, [memberView, profile, channelName]);

  useEffect(() => {
    if (!countVisit || signedIn || !profile?.ownerId) return;
    rememberQuickLinksOwner(profile.ownerId);
  }, [countVisit, signedIn, profile]);

  useEffect(() => {
    const refresh = () => {
      if (document.visibilityState !== 'visible') return;
      apiV2Instance
        .get(`/api/premium-profile/public/${encodeURIComponent(channelName)}`)
        .then((response) => setProfile(response.data))
        .catch(() => undefined);
    };
    document.addEventListener('visibilitychange', refresh);
    return () => document.removeEventListener('visibilitychange', refresh);
  }, [channelName]);

  const sendRequest = async () => {
    setBusy(true);
    setError('');
    try {
      await apiV2Instance.post('/api/premium-profile/requests', { channelName });
      setConfirmOpen(false);
      setEligible(false);
    } catch (requestError) {
      setError(readError(requestError, 'The request could not be sent.'));
    } finally {
      setBusy(false);
    }
  };

  if (missing) {
    return <Alert severity="info">That profile is not available.</Alert>;
  }
  if (!profile) return <Box sx={{ minHeight: '40vh' }} />;

  const ownerView = Boolean(user?.channelName && user.channelName === profile.channelName);
  const previewing = !countVisit && ownerView;
  const manual = profile.owned
    && profile.giveMembers === false
    && (previewing || (memberView && !ownerView));
  const buttons = memberGiftButtons({
    giveMembers: profile.giveMembers !== false,
    singleRemaining: profile.singleRemaining ?? 0,
    dualRemaining: profile.dualRemaining ?? 0,
    openRequest: previewing ? previewRequest : serverRequest,
    ownerChannel: profile.channelName,
    eligible,
  });
  const sent = sentScope ? requestSentCopy(sentScope, profile.channelName) : null;

  const ask = async (scope: 'SINGLE' | 'DUAL') => {
    if (previewing) {
      const acted = previewActOut(scope, profile.channelName);
      setPreviewRequest(acted.openRequest);
      setSentScope(scope);
      return;
    }
    setBusy(true);
    setError('');
    try {
      const response = await apiV2Instance.post('/api/premium-profile/requests', {
        channelName,
        scope,
      });
      setServerRequest(response.data?.openRequest || { scope, status: 'open' });
      setSentScope(scope);
      setProfile((current) => (
        current
          ? {
            ...current,
            giveMembers: response.data?.giveMembers,
            singleRemaining: response.data?.singleRemaining,
            dualRemaining: response.data?.dualRemaining,
          }
          : current
      ));
    } catch (requestError) {
      setError(readError(requestError, 'The request could not be sent.'));
    } finally {
      setBusy(false);
    }
  };
  const openStat = (list: RelationshipKey) => {
    if (!memberView) {
      setLeaveKind('stats');
      return;
    }
    history.push(connectionStatPath(list, channelName, ownProfile));
  };
  const leave = leaveKind ? leaveCopy({
    kind: leaveKind,
    displayName: profile.displayName,
    channelName,
    giftCode: profile.giftCode || '',
  }) : null;

  let profileMinHeight: string | undefined;
  if (countVisit && signedIn) profileMinHeight = 'calc(100vh - 54px)';
  else if (countVisit) profileMinHeight = '100vh';

  return (
    <Box sx={{
      px: { xs: 2, md: 4 },
      pb: { xs: 2, md: 4 },
      pt: 1,
      color: 'var(--qc-shell-copy, #f8f9ff)',
      display: 'flex',
      flexDirection: 'column',
      boxSizing: 'border-box',
      minHeight: profileMinHeight,
    }}
    >
      <Stack
        component="header"
        direction="row"
        alignItems="center"
        sx={{ maxWidth: 720, mx: 'auto', width: '100%', pb: 1 }}
      >
        {countVisit && <PublisherMark />}
        <ProfileMenu
          channelName={channelName}
          profileImageURL={profile.profileImageURL}
          displayName={profile.displayName}
          showAuth={countVisit && !signedIn}
          premium={profile.owned}
        />
      </Stack>
      <Stack spacing={2} sx={{ ...qcSurfaceCardSx, maxWidth: 720, mx: 'auto', p: 2 }}>
        {error && <Alert severity="error">{error}</Alert>}
        {countVisit && ownerView && (
          <QuickLinksGiftBar opener={giftOpener} />
        )}
        <ProfileIdentityCard
          displayName={profile.displayName}
          channelName={profile.channelName}
          profileImageURL={profile.profileImageURL}
          premiumLabel={profile.owned && profile.badge ? profile.badge.name : ''}
          about={profile.about}
          links={profile.links || []}
          stats={profile.stats || null}
          clickZero={memberView}
          onStat={(list) => openStat(list)}
        />
        {manual && buttons.waitingLine && <Typography>{buttons.waitingLine}</Typography>}
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
          {profile.giftAvailable && profile.visitorAutoGive !== false && ((!countVisit && !memberView) || user?.channelName !== profile.channelName) && (
            <Button
              fullWidth
              variant="contained"
              onClick={() => setLeaveKind('reserve')}
            >
              Reserve Gift Card
            </Button>
          )}
          {profile.giftAvailable && profile.visitorAutoGive === false && !memberView && (
            <Button fullWidth variant="contained" onClick={() => setSignupOpen(true)}>
              {`Join @${profile.channelName} on QuickLinks`}
            </Button>
          )}
          {countVisit && ownerView && !profile.giftAvailable && (
            <>
              <Typography>No more gift cards left</Typography>
              <Button fullWidth variant="contained" onClick={() => setGiftOpener((value) => value + 1)}>
                Add More Gift Cards
              </Button>
            </>
          )}
          {memberView
            && user?.channelName !== profile.channelName
            && eligible
            && profile.giveMembers !== false
            && ((profile.singleRemaining ?? 0) + (profile.dualRemaining ?? 0) > 0) && (
            <Button variant="contained" onClick={() => setConfirmOpen(true)}>
              Request
            </Button>
          )}
          {manual && buttons.showSingle && (
            <Button
              variant="contained"
              disabled={buttons.disabled || busy}
              onClick={() => ask('SINGLE')}
            >
              Request Single LA Pass Gift Card
            </Button>
          )}
          {manual && buttons.showDual && (
            <Button
              variant="contained"
              disabled={buttons.disabled || busy}
              onClick={() => ask('DUAL')}
            >
              Request Dual LA Pass Gift Card
            </Button>
          )}
        </Stack>
      </Stack>
      <ProfileSignupPrompt
        open={signupOpen}
        channelName={channelName}
        onClose={() => setSignupOpen(false)}
      />
      <Dialog open={Boolean(leave)} onClose={() => setLeaveKind(null)}>
        <DialogTitle>{leave?.title}</DialogTitle>
        <DialogContent>
          {leave?.lines.map((line) => (
            <Typography key={line} sx={{ mb: 1 }}>{line}</Typography>
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLeaveKind(null)}>Close</Button>
          <Button
            variant="contained"
            onClick={() => {
              if (!countVisit || !leave) {
                setLeaveKind(null);
                return;
              }
              window.location.assign(leave.path);
            }}
          >
            {leave?.action}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={Boolean(sent)} onClose={() => setSentScope(null)}>
        <DialogTitle>{sent?.title}</DialogTitle>
        <DialogContent>
          <Typography>{sent?.body}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSentScope(null)}>Close</Button>
        </DialogActions>
      </Dialog>
      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>Request a gift card</DialogTitle>
        <DialogContent>
          This asks the profile owner to send one Lifetime Access gift card. It uses
          one of your request slots.
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>Close</Button>
          <Button disabled={busy} variant="contained" onClick={() => sendRequest()}>
            Confirm request
          </Button>
        </DialogActions>
      </Dialog>
      {countVisit && <ProfileFooter />}
    </Box>
  );
}

PublicProfilePage.defaultProps = {
  audience: undefined,
  countVisit: true,
  refreshToken: 0,
};

export default PublicProfilePage;
